import { doNothing } from 'remeda'
import { assign, fromPromise, setup } from 'xstate'

import { debugLog } from '../../utils/helpers'

export const INITIAL = 'initial'
export const DEFAULT = 'default'
export const PROCESS = 'process'
export const FINALIZATION = 'finalization'
export const CONSENT = 'consent'
export const END = 'end'
export const ERROR = 'error'

export type RejectEvent = { type: 'REJECT' }
export type ProceedEvent = { type: 'PROCEED' }
export type CompletedEvent = { type: 'COMPLETED' }
export type GiveConsentEvent = { type: 'CONSENT_GIVEN' }
export type CancelConsentEvent = { type: 'CANCEL_CONSENT' }
export type ConfirmErrorEvent = { type: 'CONFIRM_ERROR' }

export type StatusEvent =
  | RejectEvent
  | ProceedEvent
  | GiveConsentEvent
  | ConfirmErrorEvent
  | CancelConsentEvent
  | CompletedEvent

export type StatusStepContext = {
  error?: object
  isConsentShown?: boolean
  hasConsent: boolean
}

export interface StatusStepActions {
  proceed: () => void
  reject?: () => void

  /** Provide if you need to process something between initial submit and consent. For example convert wizard data to request */
  processData?: () => Promise<unknown>
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const statusStepMachine = ({ proceed, reject, processData }: StatusStepActions) => {
  return setup({
    types: {
      context: {} as StatusStepContext,
      events: {} as StatusEvent,
      input: {} as StatusStepContext,
    },
    guards: {
      hasConsent: ({ context }) => {
        return context.hasConsent
      },
    },
    actions: {
      proceed,
      reject: () => (reject ? reject() : doNothing()),
      giveConsent: assign(() => {
        return {
          hasConsent: true,
        }
      }),
      removeError: assign(() => {
        return {
          error: undefined,
        }
      }),
    },
    actors: {
      processData: fromPromise(async () => {
        debugLog('Service: processData')
        return processData ? processData() : Promise.resolve()
      }),
    },
  }).createMachine({
    initial: DEFAULT,
    context: ({ input }) => input,
    states: {
      [DEFAULT]: {
        on: {
          COMPLETED: { target: FINALIZATION },
        },
      },
      [PROCESS]: {
        invoke: {
          src: 'processData',
          onDone: [
            {
              target: CONSENT,
            },
          ],
          onError: {
            target: ERROR,
          },
        },
      },
      [FINALIZATION]: {
        on: {
          REJECT: { actions: { type: 'reject' } },
          PROCEED: [{ guard: 'hasConsent', actions: { type: 'proceed' } }, { target: PROCESS }],
        },
      },
      [CONSENT]: {
        on: {
          CANCEL_CONSENT: { target: FINALIZATION },
          CONSENT_GIVEN: { target: END, actions: [{ type: 'giveConsent' }, { type: 'proceed' }] },
        },
      },
      [ERROR]: {
        on: {
          CONFIRM_ERROR: { target: FINALIZATION, actions: 'removeError' },
        },
      },
      [END]: {},
    },
  })
}
